import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { PainelMe, PeriodQuery } from '@/api/painel';
import { DEFAULT_PRESET } from '@/api/painel';

export const PainelMeContext = createContext<PainelMe | null>(null);

export function usePainelMe(): PainelMe {
  const me = useContext(PainelMeContext);
  if (!me) throw new Error('usePainelMe fora do RequirePainel');
  return me;
}

/** Período vem da URL (`?preset=` ou `?date_from=&date_to=`): troca de aba
 *  mantém o mesmo recorte e o link pode ser compartilhado. */
export function usePainelPeriod(): [PeriodQuery, (p: PeriodQuery) => void] {
  const [params, setParams] = useSearchParams();
  const dateFrom = params.get('date_from') ?? undefined;
  const dateTo = params.get('date_to') ?? undefined;
  const period: PeriodQuery =
    dateFrom && dateTo
      ? { date_from: dateFrom, date_to: dateTo }
      : { preset: params.get('preset') ?? DEFAULT_PRESET };
  const setPeriod = useCallback(
    (p: PeriodQuery) => {
      const next = new URLSearchParams(params);
      next.delete('preset');
      next.delete('date_from');
      next.delete('date_to');
      if (p.date_from && p.date_to) {
        next.set('date_from', p.date_from);
        next.set('date_to', p.date_to);
      } else if (p.preset) {
        next.set('preset', p.preset);
      }
      next.delete('page');
      setParams(next, { replace: true });
    },
    [params, setParams]
  );
  return [period, setPeriod];
}

export interface Loaded<T> {
  data: T | null;
  error: string | null;
  /** Recarregando com dado anterior na tela (o frame não pisca). */
  loading: boolean;
  reload: () => void;
}

/**
 * Busca com "refetch mantém o frame": enquanto recarrega, `data` segue sendo o
 * último resultado bom (a tela esmaece em vez de virar skeleton). Respostas
 * fora de ordem (período trocado rápido) são descartadas.
 */
export function useLoad<T>(fetcher: () => Promise<T>, deps: unknown[]): Loaded<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const seq = useRef(0);

  useEffect(() => {
    const my = ++seq.current;
    setLoading(true);
    fetcher()
      .then((d) => {
        if (my !== seq.current) return;
        setData(d);
        setError(null);
      })
      .catch((e: unknown) => {
        if (my !== seq.current) return;
        const status = (e as { status?: number } | null)?.status;
        setError(
          status === 404
            ? 'Você não tem acesso a esta organização (ou o painel está desligado).'
            : e instanceof Error && e.message
              ? e.message
              : 'Falha ao carregar.'
        );
      })
      .finally(() => {
        if (my === seq.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  return { data, error, loading, reload: () => setTick((t) => t + 1) };
}
